import { createFileRoute, Link } from "@tanstack/react-router";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { pageHead, breadcrumbJsonLd, webPageJsonLd, ldScript, absoluteUrl } from "@/lib/seo";

const PATH = "/privacy";
const TITLE = "Privacy Policy — ReadBarcode.com";
const DESC =
  "Read the ReadBarcode.com Privacy Policy to understand how browser-based barcode tools, local processing, analytics, cookies, contact data, Cloudflare hosting, GDPR rights, and CCPA rights are handled.";

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Privacy", path: PATH },
];

const EFFECTIVE_DATE = "2026-05-25";
const LAST_UPDATED = "2026-07-29";
const BREADCRUMB_ID = `${absoluteUrl(PATH)}#breadcrumb`;

export const Route = createFileRoute("/privacy")({
  component: PrivacyPage,
  head: () => ({
    ...pageHead({
      title: TITLE,
      description: DESC,
      path: PATH,
      ogType: "website",
      ogImage: absoluteUrl("/og/privacy.jpg"),
      ogImageAlt:
        "ReadBarcode.com Privacy Policy — browser-based barcode processing, GDPR rights, CCPA rights and Cloudflare hosting disclosure.",
    }),
    scripts: [
      ldScript(
        webPageJsonLd({
          path: PATH,
          title: TITLE,
          description: DESC,
          datePublished: EFFECTIVE_DATE,
          dateModified: LAST_UPDATED,
          breadcrumbId: BREADCRUMB_ID,
        }),
      ),
      ldScript(breadcrumbJsonLd(CRUMBS, BREADCRUMB_ID)),
    ],
  }),
});

function PrivacyPage() {
  return (
    <ToolLayout
      eyebrow="Legal"
      title="Privacy Policy"
      description="How browser-based barcode processing, analytics, cookies, contact data, Cloudflare hosting, GDPR rights and CCPA rights are handled at ReadBarcode.com. In short, the barcodes you scan or generate are processed in your browser, and this policy explains the few cases where data reaches a server."
      crumbs={CRUMBS}
      content={
        <>
          <p>
            {`Effective date: ${formatDate(EFFECTIVE_DATE)} · Last updated: ${formatDate(LAST_UPDATED)}`}
          </p>
          <p>
            ReadBarcode.com is an independent browser-based barcode toolkit. This Privacy Policy
            explains what information is and is not processed when you use the site, how that
            information is handled, and the rights you have over it. <strong>
              We do not sell your personal information, and we do not share it for cross-context
              behavioral advertising.
            </strong> The full CCPA / CPRA disclosure is in the section <a href="#do-not-sell">We do not sell your personal information</a> below.
          </p>

          <h2>Who We Are</h2>
          <p>
            The data controller for ReadBarcode.com is the independent operator of ReadBarcode.com.
            For privacy-related inquiries, please use the contact methods provided on the <Link to="/contact">Contact page</Link>.
          </p>

          <h2>What This Policy Covers</h2>
          <p>
            This policy applies to your use of the website at <strong>https://readbarcode.com</strong>
            , all tools, generators, validators, guides and the installable Progressive Web App
            offered there. References to &ldquo;personal information&rdquo; in this policy mean
            information that identifies, relates to, or can reasonably be linked to a specific
            individual.
          </p>

          <h2>Browser-Based Barcode Processing</h2>
          <p>
            Most barcode reading, image decoding, barcode generation, and export actions happen
            directly in your browser. Camera scans, uploaded images, generated barcodes, and entered
            barcode values are not uploaded to our servers for normal tool use.
          </p>

          <h2>Information We Do Not Collect During Normal Tool Use</h2>
          <p>
            ReadBarcode.com does not track, store, sell, or analyze scanned barcode values, uploaded
            barcode images, generated barcode content, inventory lists, or user-entered barcode values
            for normal tool usage. In particular, the following stay on your device and are never
            transmitted to us:
          </p>
          <ul>
            <li>Camera frames captured by the readers</li>
            <li>Images you upload for decoding</li>
            <li>Decoded barcode values</li>
            <li>GTINs, UPCs, EANs and ISBNs you paste into validators</li>
            <li>Generated barcode, QR and Data Matrix payloads</li>
            <li>Wi-Fi credentials, vCard contact details and other generator inputs</li>
            <li>Inventory and batch lists held in browser storage</li>
          </ul>

          <h2>Information You May Choose to Send Us</h2>
          <p>
            If you contact us by email or through the contact form on the <Link to="/contact">Contact page</Link>, we receive the information you choose to send,
            such as your name, email address, the category you selected, and the body of your message.
            We use that information solely to respond to your enquiry and to keep a record of the
            correspondence for the retention period set out below.
          </p>

          <h2>Legal Basis for Processing (GDPR)</h2>
          <p>We process limited information under the following legal bases:</p>
          <ul>
            <li>
              <strong>Legitimate interest</strong> (Article 6(1)(f) GDPR): aggregate analytics,
              security logs, performance monitoring, and abuse prevention. Our interest is keeping the
              platform safe, reliable, and continuously improved. This processing does not include
              barcode payloads, uploaded images, or other tool input data.
            </li>
            <li>
              <strong>Performance of a request</strong> (Article 6(1)(b) GDPR): when you contact us,
              we process your message, email address, and any details you choose to send in order to
              respond.
            </li>
            <li>
              <strong>Consent</strong> (Article 6(1)(a) GDPR): if optional features are introduced in
              the future that require consent (such as marketing communications or non-essential
              cookies), processing will only occur after you give explicit consent.
            </li>
          </ul>

          <h2>Analytics and Technical Logs</h2>
          <p>
            We use aggregate analytics and technical logs to understand site performance, security,
            and general usage patterns. Our analytics provider is <a
              href="https://marketingplatform.google.com/about/analytics/"
              rel="noopener noreferrer"
              target="_blank"
            >
              Google Analytics 4
            </a>
            , configured in a cookieless, privacy-reduced mode:
          </p>
          <ul>
            <li>
              <strong>No cookies and no browser storage</strong>: the tag runs with storage disabled,
              so it does not set <code>_ga</code> cookies or write to localStorage. A temporary
              identifier exists only for the duration of a single page load and is discarded
              afterwards, which means we measure visits rather than identifiable returning users.
            </li>
            <li>
              <strong>No advertising features</strong>: Google Signals, ad personalisation, and
              advertising storage are all disabled, and no data is used for remarketing or
              cross-context behavioural advertising.
            </li>
            <li>
              <strong>IP anonymisation</strong> is enabled; we never receive or store full visitor IP
              addresses through analytics.
            </li>
            <li>
              <strong>Loaded after the page</strong>: the analytics script is only requested once the
              page is fully usable, so it does not affect the performance of the tools.
            </li>
          </ul>
          <p>
            What we see is limited to aggregate data such as which pages are viewed, approximate
            country, device category, browser, and referring source. These analytics do not include
            the barcode values, uploaded images, generated codes, inventory lists, or other tool
            inputs you process in the browser. In particular, analytics never include:
          </p>
          <ul>
            <li>Scanned barcode values</li>
            <li>Uploaded barcode images</li>
            <li>Generated barcode content</li>
            <li>Inventory lists</li>
            <li>Wi-Fi passwords</li>
            <li>vCard contact data</li>
            <li>Barcode and QR payloads</li>
            <li>User-entered barcode values</li>
          </ul>

          <h2>Cookies and Browser Storage</h2>
          <p>
            ReadBarcode.com does not use advertising or third-party tracking cookies. We use only
            strictly necessary cookies and browser storage for site operation, tool preferences, and
            security. Our Google Analytics tag is deliberately configured without cookies or browser
            storage, as described in the section above, so it does not place non-essential cookies on
            your device.
          </p>
          <p>
            Cloudflare, our hosting and security provider, may set strictly necessary cookies (such as <code>__cf_bm</code> for bot management) that are exempt from consent requirements under
            the ePrivacy Directive because they are essential to the security and operation of the
            service.
          </p>
          <p>
            If we introduce non-essential cookies in the future (for example, analytics that require
            consent or advertising cookies), we will display a clear consent banner before any such
            cookies are set, and you will be able to refuse them without losing access to core tools.
          </p>
          <p>
            Some tools may use local browser storage (<em>localStorage</em> or <em>sessionStorage</em>
            ) to remember preferences, temporary lists, or local settings such as your most recent
            generator configuration. This data remains on your device, is not transmitted to us, and
            can be cleared at any time through your browser&apos;s settings.
          </p>

          <h2>Cloudflare and Infrastructure Providers</h2>
          <p>
            ReadBarcode.com is hosted and delivered through Cloudflare. Cloudflare may process
            technical data such as IP address, request headers, security events, and performance logs
            as part of hosting, caching, security, and content delivery. Cloudflare&apos;s data
            processing is governed by Cloudflare&apos;s own privacy policy and data processing
            agreements.
          </p>

          <h2>Third-Party Services</h2>
          <p>
            The core barcode tools are designed to avoid third-party processing of barcode input data.
            Two third-party processors are involved in normal operation: Cloudflare, which provides
            hosting, content delivery, and security services, and Google (Google Analytics 4), which
            processes the aggregate, cookieless usage measurements described above. Google&apos;s
            processing is governed by its own privacy policy, available at <a href="https://policies.google.com/privacy" rel="noopener noreferrer" target="_blank">
              policies.google.com/privacy
            </a>
            . If future features connect to external lookup APIs or other processors, those services
            will be disclosed in this policy before they are activated.
          </p>
          <p>
            Form submissions through the contact page are processed by <a href="https://resend.com" rel="noopener noreferrer" target="_blank">
              Resend
            </a>
            , a transactional email service used to deliver contact messages to the operator. Resend
            processes only the contact form fields you submit (name, email, subject category, message,
            and optional URL) for the purpose of email delivery. Resend&apos;s privacy policy is
            available at <a
              href="https://resend.com/legal/privacy-policy"
              rel="noopener noreferrer"
              target="_blank"
            >
              resend.com/legal/privacy-policy
            </a>
            .
          </p>

          <h2>Data Retention Periods</h2>
          <p>We retain different categories of data for different periods:</p>
          <ul>
            <li>
              <strong>Contact messages</strong>: retained for up to 24 months from the date of last
              correspondence, then deleted or anonymized. This allows us to provide continued support
              and reference for unresolved inquiries.
            </li>
            <li>
              <strong>Security and abuse logs</strong> (IP addresses, request patterns associated with
              security events): retained for up to 12 months for security investigation and abuse
              prevention.
            </li>
            <li>
              <strong>Aggregate analytics</strong>: retained indefinitely in anonymized, aggregated
              form that cannot identify individual users.
            </li>
            <li>
              <strong>Cloudflare-side technical logs</strong>: retention is governed by
              Cloudflare&apos;s own policies and may include limited request metadata for up to 30
              days.
            </li>
          </ul>
          <p>
            Browser storage data on your device is retained until you clear it through your browser.
          </p>

          <h2>International Data Transfers</h2>
          <p>
            ReadBarcode.com uses Cloudflare for hosting and content delivery. Cloudflare operates a
            global network of data centers, which means technical data such as IP addresses and
            request metadata may be processed in multiple countries, including outside the EEA.
            Cloudflare relies on appropriate safeguards for international data transfers, including
            Standard Contractual Clauses approved by the European Commission, as described in
            Cloudflare&apos;s own privacy documentation.
          </p>

          <h2>Your Privacy Rights (EEA / UK / Switzerland)</h2>
          <p>
            If you are located in the European Economic Area (EEA), the United Kingdom, or
            Switzerland, you have the following rights under applicable data protection laws regarding
            personal data we hold about you:
          </p>
          <ul>
            <li>
              <strong>Right of access</strong>: request a copy of the personal information we hold
              about you.
            </li>
            <li>
              <strong>Right to rectification</strong>: request correction of inaccurate or incomplete
              information.
            </li>
            <li>
              <strong>Right to erasure</strong> (&ldquo;right to be forgotten&rdquo;): request
              deletion of your personal information, subject to legal retention obligations.
            </li>
            <li>
              <strong>Right to restriction</strong>: request that we limit how we process your
              information.
            </li>
            <li>
              <strong>Right to data portability</strong>: receive your information in a structured,
              commonly used format.
            </li>
            <li>
              <strong>Right to object</strong>: object to processing based on legitimate interest.
            </li>
            <li>
              <strong>Right to withdraw consent</strong>: withdraw consent at any time where
              processing is based on consent (without affecting the lawfulness of prior processing).
            </li>
          </ul>
          <p>
            To exercise any of these rights, please contact us through the <Link to="/contact">Contact page</Link>. We will respond within 30 days as required by
            GDPR.
          </p>
          <p>
            You also have the right to lodge a complaint with your local data protection supervisory
            authority if you believe our processing of your personal information violates applicable
            law. You can find your national authority via the <a href="https://edpb.europa.eu/" rel="noopener noreferrer" target="_blank">
              European Data Protection Board
            </a>
            .
          </p>

          <h2>Your Rights as a California Resident (CCPA / CPRA)</h2>
          <p>
            If you are a California resident, the California Consumer Privacy Act (CCPA) and
            California Privacy Rights Act (CPRA) provide you with specific rights regarding your
            personal information:
          </p>
          <ul>
            <li>
              <strong>Right to know</strong> what personal information we collect, use, and disclose.
            </li>
            <li>
              <strong>Right to delete</strong> personal information we have collected from you
              (subject to exceptions).
            </li>
            <li>
              <strong>Right to correct</strong> inaccurate personal information.
            </li>
            <li>
              <strong>Right to opt out of sale or sharing</strong> — although we do not sell or share
              personal information for cross-context behavioral advertising, you have the right to
              confirm this.
            </li>
            <li>
              <strong>Right to limit use of sensitive personal information</strong> — we do not use
              sensitive personal information beyond what is necessary to provide the service.
            </li>
            <li>
              <strong>Right to non-discrimination</strong> for exercising any of these rights.
            </li>
          </ul>
          <p>
            To exercise these rights, please contact us through the <Link to="/contact">Contact page</Link>.
          </p>

          <h2 id="do-not-sell">We Do Not Sell Your Personal Information</h2>
          <p>
            <strong>We do not sell your personal information.</strong> We do not sell barcode values,
            uploaded images, generated barcodes, contact details, or user-submitted information to any
            third party. We do not share personal information for cross-context behavioral
            advertising. This statement applies to all users, including California residents under the
            California Consumer Privacy Act (CCPA) and the California Privacy Rights Act (CPRA).
          </p>

          <h2>Children&apos;s Privacy</h2>
          <p>
            ReadBarcode.com is not directed to children under 13 in the United States or under the
            applicable age of digital consent in your country (which may be 13, 14, 15, or 16
            depending on the EU member state). We do not knowingly collect personal information from
            children. If you believe a child has provided personal information through the contact
            page, please contact us and we will delete the information promptly.
          </p>

          <h2>Security and Security Incident Notification</h2>
          <p>
            We take reasonable technical and organizational measures to protect information
            transmitted to or processed by ReadBarcode.com, including HTTPS encryption for all
            traffic, security protections provided by Cloudflare, and limited access to any submitted
            contact data.
          </p>
          <p>
            However, no method of transmission over the internet or electronic storage is completely
            secure. We cannot guarantee absolute security, and you transmit information at your own
            risk.
          </p>
          <p>
            Users should avoid entering sensitive personal, medical, legal, confidential, or regulated
            information into any online tool unless they fully understand how the tool works. Although
            core tools process data in your browser, you remain responsible for the information you
            choose to enter or upload.
          </p>
          <p>
            If we become aware of a security incident affecting personal information you have
            submitted to us, we will notify affected users without undue delay where required by
            applicable law, and we will report the incident to relevant supervisory authorities as
            required under GDPR Article 33 and other applicable regulations.
          </p>

          <h2>Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices,
            services, or legal obligations. When we make material changes, we will update the
            &ldquo;Last updated&rdquo; date at the top of this policy and, where appropriate, provide
            additional notice. We encourage you to review this policy periodically.
          </p>

          <h2>Contact</h2>
          <p>
            For privacy questions, data subject requests, or notices about this policy, please use the <Link to="/contact">Contact page</Link> with the subject line &ldquo;Privacy
            request&rdquo;. We aim to respond within 30 days as required by GDPR.
          </p>
        </>
      }
    />
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" });
}
